import InputField from "@/components/InputField";
import TextareaField from "@/components/TextArea";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";


import { notifyError, notifySuccess } from "@/lib/notification";
import { cn } from "@/lib/utils";
import { authService } from "@/services/auth.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const registerSchema = z.object({
  company_name: z
    .string()
    .min(2, "Company Name must be at least 2 characters")
    .trim(),
  company_email: z
    .string()
    .min(1, "Company Email is required")
    .email("Please enter a valid Company Email")
    .trim(),
  company_phone: z
    .string()
    .regex(/^\d{10}$/, "Company Phone must be exactly 10 digits"),
  company_address: z
    .string()
    .min(5, "Company Address must be at least 5 characters")
    .trim(),
  company_website: z
    .string()
    .url("Please enter a valid URL (e.g. https://example.com)")
    .trim()
    .optional()
    .or(z.literal("")),
  admin_name: z
    .string()
    .min(2, "Admin Name must be at least 2 characters")
    .trim(),
  admin_org_email: z
    .string()
    .min(1, "Org Email is required")
    .email("Please enter a valid organizational email")
    .trim(),
  admin_personal_email: z
    .string()
    .email("Please enter a valid personal email")
    .optional()
    .or(z.literal("")),
  admin_phone: z
    .string()
    .regex(/^\d{10}$/, "Admin Phone must be exactly 10 digits"),
});

type RegisterFormType = z.infer<typeof registerSchema>;

const RegisterCollege = () => {
  const navigate = useNavigate();



  const form = useForm<RegisterFormType>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      company_name: "",
      company_email: "",
      company_phone: "",
      company_address: "",
      company_website: "",
      admin_name: "",
      admin_org_email: "",
      admin_personal_email: "",
      admin_phone: "",
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterFormType) => {
      return await authService.registerCollege(data);
    },
    onSuccess: (data, variables) => {
      // No approval step any more: the account is live the moment this returns,
      // so send them straight to login with the admin email already filled in.
      notifySuccess(
        data?.message ||
          "Registration complete. You can log in now using this admin email."
      );
      navigate("/login", {
        state: { identifier: variables.admin_org_email },
      });
    },
    onError: (error: any) => {
      notifyError(
        error.response?.data?.error?.message ||
        error?.message ||
        "Registration failed. Please try again."
      );
    },
  });

  const onSubmit = (data: RegisterFormType) => {
    const payload = { ...data }
    if (!payload.admin_personal_email) {
      delete payload.admin_personal_email
    }
    registerMutation.mutate(payload);
  };

  return (
    <div className="flex min-h-screen w-screen flex-col items-center justify-center bg-neutral-50 p-6">
      <div className="w-full max-w-2xl rounded-xl border border-neutral-300 bg-white p-8 shadow">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold">Register Recruiter</h1>
          <p className="mt-2 text-sm text-neutral-500">
            Fill out the details below to complete your registration.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* College Details Section */}
            <div>
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-neutral-800">
                Recruiter Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Maps to payload: college_name */}
                <InputField
                  type="text"
                  name="company_name"
                  label="Company Name"
                  placeholder="Enter company name"
                  disabled={registerMutation.isPending}
                  required
                  autoFocus
                />
                {/* Maps to payload: college_email */}
                <InputField
                  type="email"
                  name="company_email"
                  label="Company Email"
                  placeholder="Enter company email"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: college_phone */}
                <InputField
                  type="tel"
                  name="company_phone"
                  label="Company Phone"
                  placeholder="Enter phone number"
                  disabled={registerMutation.isPending}
                  required
                />

                {/* Maps to payload: college_website */}
                <InputField
                  type="url"
                  name="company_website"
                  label="Company Website"
                  placeholder="https://example.com"
                  disabled={registerMutation.isPending}
                  className={{ itemClass: "md:col-span-2" }}
                />

                {/* Maps to payload: college_address */}
                <TextareaField
                  name="company_address"
                  label="Company Address"
                  placeholder="Enter full address"
                  disabled={registerMutation.isPending}
                  className={{ itemClass: "md:col-span-2" }}
                  required
                />
              </div>
            </div>

            {/* Admin Details Section */}
            <div>
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-neutral-800">
                Admin Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Maps to payload: admin_name */}
                <InputField
                  type="text"
                  name="admin_name"
                  label="Admin Name"
                  placeholder="Enter full name"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_phone */}
                <InputField
                  type="tel"
                  name="admin_phone"
                  label="Admin Phone"
                  placeholder="Enter admin phone number"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_org_email */}
                <InputField
                  type="email"
                  name="admin_org_email"
                  label="Org Email"
                  placeholder="admin@college.edu"
                  disabled={registerMutation.isPending}
                  required
                />
                {/* Maps to payload: admin_personal_email */}
                <InputField
                  type="email"
                  name="admin_personal_email"
                  label="Personal Email"
                  placeholder="admin@gmail.com"
                  disabled={registerMutation.isPending}
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3">
              <Button
                type="submit"
                className={cn(
                  "w-full transition-colors",
                  registerMutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                disabled={registerMutation.isPending}
              >
                {registerMutation.isPending
                  ? "Submitting..."
                  : "Submit Registration"}
              </Button>

              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full transition-colors",
                  registerMutation.isPending
                    ? "opacity-50 hover:cursor-not-allowed"
                    : "hover:cursor-pointer"
                )}
                onClick={() => navigate("/login")}
                disabled={registerMutation.isPending}
              >
                Back to Login
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default RegisterCollege;
